<template>
  <div>
    <!-- Hero Section (2-column: text + browser mockup) -->
    <section class="relative overflow-hidden min-h-screen flex items-center pt-16">
      <div class="absolute inset-0 bg-white dark:bg-dark transition-colors duration-300">
        <img v-if="isDark" src="/website_bg.webp" alt="" class="absolute inset-0 w-full h-full object-cover" />
        <img v-else src="/website_bg_light.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-40" />
      </div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p class="text-[13px] uppercase tracking-[0.2em] text-blue-500 font-medium mb-6">
              {{ $t('websiteSolution.specialOffer') }}
            </p>
            <h1 class="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-4">
              <span class="text-gray-900 dark:text-white transition-colors duration-300">{{ $t('website.hero.title') }}</span>
              <br />
              <span class="hero-gradient-text">{{ $t('website.hero.punchline') }}</span>
            </h1>
            <p class="text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-xl leading-relaxed mb-10 transition-colors duration-300">
              {{ $t('website.hero.description') }}
            </p>
            <div class="flex flex-col sm:flex-row gap-4 items-start mb-10">
              <router-link to="/contact" class="inline-flex items-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold text-lg transition-colors duration-300">
                <Rocket class="mr-3 h-6 w-6" />
                {{ $t('website.hero.cta') }}
              </router-link>
              <router-link to="/preventivo-sito-web" class="inline-flex items-center h-14 px-8 border border-gray-200 dark:border-white/[0.08] text-gray-500 dark:text-gray-400 rounded-xl font-bold text-lg transition-colors duration-300">
                <Globe class="mr-2 h-5 w-5" />
                {{ $t('websiteSolution.cta.quote') }}
              </router-link>
            </div>
            <div class="flex flex-wrap gap-6 text-sm text-gray-500 dark:text-gray-400">
              <div class="flex items-center"><Check class="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.freeChatbot') }}</div>
              <div class="flex items-center"><Check class="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.seo') }}</div>
              <div class="flex items-center"><Check class="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.support') }}</div>
            </div>
          </div>

          <!-- Animated Browser Mockup with 3D parallax -->
          <div ref="heroMockup" class="relative hidden lg:block" data-animate="hero" :class="{ 'is-visible': visible.hero }" @mousemove="onHeroMove" @mouseleave="onHeroLeave">
            <div class="absolute -inset-20 pointer-events-none"><div class="w-72 h-72 rounded-full bg-blue-500/[0.06] blur-3xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" :style="{ transform: `translate(calc(-50% + ${mx * 30}px), calc(-50% + ${my * 30}px))` }"></div></div>
            <div class="mockup-browser rounded-xl border border-gray-200 dark:border-white/[0.08] overflow-hidden shadow-xl bg-white dark:bg-dark transition-colors duration-300" :style="mockupTransform">
              <div class="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                <div class="flex gap-1.5">
                  <div class="w-2.5 h-2.5 rounded-full bg-red-400/80"></div>
                  <div class="w-2.5 h-2.5 rounded-full bg-yellow-400/80"></div>
                  <div class="w-2.5 h-2.5 rounded-full bg-green-400/80"></div>
                </div>
                <div class="flex-1 h-6 rounded-md bg-white dark:bg-white/[0.06] mx-8 flex items-center px-3">
                  <Lock class="w-3 h-3 text-green-500 mr-1.5" />
                  <span class="text-[11px] text-gray-400 dark:text-gray-500">tuodominio.com</span>
                </div>
              </div>
              <div class="p-5 space-y-4">
                <div class="flex items-center justify-between anim-el d1">
                  <span class="text-[11px] font-bold text-gray-900/60 dark:text-white/40 tracking-wide">TuoSito</span>
                  <div class="flex gap-4 items-center">
                    <span class="text-[9px] text-gray-400 dark:text-gray-500">Servizi</span>
                    <span class="text-[9px] text-gray-400 dark:text-gray-500">Chi Siamo</span>
                    <div class="h-6 w-16 rounded bg-blue-500/80 flex items-center justify-center"><span class="text-[8px] text-white font-bold">Contatti</span></div>
                  </div>
                </div>
                <div class="pt-6 pb-2 anim-el d2">
                  <p class="text-sm font-bold text-gray-900/70 dark:text-white/60 mb-1">Il tuo business merita</p>
                  <p class="text-sm font-bold text-gray-900/70 dark:text-white/60 mb-3">un sito <span class="text-blue-500">che converte.</span></p>
                  <p class="text-[9px] text-gray-400 dark:text-gray-500 mb-1">Design moderno, SEO ottimizzato e velocità fulminea.</p>
                  <p class="text-[9px] text-gray-400 dark:text-gray-500 mb-5">Trasforma i visitatori in clienti oggi stesso.</p>
                  <div class="flex gap-3">
                    <div class="h-8 w-28 rounded-lg bg-gray-900 dark:bg-white flex items-center justify-center"><span class="text-[8px] font-bold text-white dark:text-gray-900">Inizia Ora</span></div>
                    <div class="h-8 w-24 rounded-lg border border-gray-200 dark:border-white/[0.1] flex items-center justify-center"><span class="text-[8px] font-bold text-gray-500 dark:text-gray-400">Portfolio</span></div>
                  </div>
                </div>
                <div class="grid grid-cols-3 gap-3 anim-el d3">
                  <div class="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-3">
                    <div class="w-6 h-6 rounded bg-blue-500/10 flex items-center justify-center mb-2"><Zap class="w-3 h-3 text-blue-500" /></div>
                    <p class="text-[8px] font-bold text-gray-700 dark:text-gray-300 mb-0.5">Velocità</p>
                    <p class="text-[7px] text-gray-400 dark:text-gray-500">Caricamento &lt;1s</p>
                  </div>
                  <div class="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-3">
                    <div class="w-6 h-6 rounded bg-green-500/10 flex items-center justify-center mb-2"><Search class="w-3 h-3 text-green-500" /></div>
                    <p class="text-[8px] font-bold text-gray-700 dark:text-gray-300 mb-0.5">SEO</p>
                    <p class="text-[7px] text-gray-400 dark:text-gray-500">Prima pagina Google</p>
                  </div>
                  <div class="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-3">
                    <div class="w-6 h-6 rounded bg-purple-500/10 flex items-center justify-center mb-2"><TrendingUp class="w-3 h-3 text-purple-500" /></div>
                    <p class="text-[8px] font-bold text-gray-700 dark:text-gray-300 mb-0.5">Conversioni</p>
                    <p class="text-[7px] text-gray-400 dark:text-gray-500">+300% lead</p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Floating metric badges -->
            <div class="absolute -top-4 -right-4 anim-el d4 floating-badge fb1" :style="badge1Transform">
              <div class="rounded-xl bg-white dark:bg-dark-50 border border-gray-200 dark:border-white/[0.08] shadow-lg px-3 py-2 flex items-center gap-2">
                <div class="w-7 h-7 rounded-full bg-green-500/10 flex items-center justify-center"><Zap class="w-3.5 h-3.5 text-green-500" /></div>
                <div><div class="text-[10px] text-gray-400">PageSpeed</div><div class="text-xs font-bold text-gray-900 dark:text-white">98/100</div></div>
              </div>
            </div>
            <div class="absolute -bottom-4 -left-4 anim-el d5 floating-badge fb2" :style="badge2Transform">
              <div class="rounded-xl bg-white dark:bg-dark-50 border border-gray-200 dark:border-white/[0.08] shadow-lg px-3 py-2 flex items-center gap-2">
                <div class="w-7 h-7 rounded-full bg-blue-500/10 flex items-center justify-center"><Search class="w-3.5 h-3.5 text-blue-500" /></div>
                <div><div class="text-[10px] text-gray-400">SEO</div><div class="text-xs font-bold text-gray-900 dark:text-white">A+</div></div>
              </div>
            </div>
            <div class="absolute top-1/2 -right-6 anim-el d6 floating-badge fb3" :style="badge3Transform">
              <div class="rounded-xl bg-white dark:bg-dark-50 border border-gray-200 dark:border-white/[0.08] shadow-lg px-3 py-2 flex items-center gap-2">
                <div class="w-7 h-7 rounded-full bg-purple-500/10 flex items-center justify-center"><TrendingUp class="w-3.5 h-3.5 text-purple-500" /></div>
                <div><div class="text-[10px] text-gray-400">Conversioni</div><div class="text-xs font-bold text-gray-900 dark:text-white">+300%</div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-dark to-transparent z-10"></div>
    </section>

    <!-- Value Proposition (always dark) + animated dashboard -->
    <section class="relative py-28 bg-gray-950 overflow-hidden" data-animate="valueprop" :class="{ 'is-visible': visible.valueprop }">
      <!-- Floating particles -->
      <div class="absolute inset-0 pointer-events-none">
        <div class="particle" style="left:8%; animation-duration:14s; animation-delay:0s"></div>
        <div class="particle" style="left:22%; animation-duration:18s; animation-delay:3s"></div>
        <div class="particle" style="left:40%; animation-duration:16s; animation-delay:1s"></div>
        <div class="particle" style="left:55%; animation-duration:20s; animation-delay:5s"></div>
        <div class="particle" style="left:72%; animation-duration:15s; animation-delay:2s"></div>
        <div class="particle" style="left:88%; animation-duration:17s; animation-delay:4s"></div>
      </div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-4xl mx-auto text-center mb-16">
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">{{ $t('website.valueProp.heading') }}</h2>
          <p class="text-lg md:text-xl text-white/80 leading-relaxed">{{ $t('website.valueProp.description') }}</p>
        </div>

        <!-- Animated Dashboard Mockup -->
        <div class="max-w-3xl mx-auto mb-16">
          <div class="rounded-xl border border-white/[0.08] overflow-hidden bg-white/[0.03] backdrop-blur-sm">
            <div class="flex items-center gap-2 px-4 py-2 border-b border-white/[0.06]">
              <div class="flex gap-1.5">
                <div class="w-2 h-2 rounded-full bg-white/20"></div>
                <div class="w-2 h-2 rounded-full bg-white/20"></div>
                <div class="w-2 h-2 rounded-full bg-white/20"></div>
              </div>
              <div class="flex-1 h-5 rounded bg-white/[0.04] mx-6 flex items-center px-2">
                <Lock class="w-2.5 h-2.5 text-green-400 mr-1" />
                <span class="text-[10px] text-white/30">analytics.tuodominio.com</span>
              </div>
            </div>
            <div class="p-5">
              <!-- Animated metrics row with live counters -->
              <div class="grid grid-cols-3 gap-3 mb-5 anim-el d1">
                <div class="rounded-lg bg-white/[0.04] border border-white/[0.06] p-3">
                  <div class="text-[10px] text-white/40 mb-1">Visitatori</div>
                  <div class="text-lg font-bold text-white tabular-nums">{{ cVisitors.toLocaleString('it-IT') }}</div>
                  <div class="text-[10px] text-green-400 flex items-center gap-0.5"><TrendingUp class="w-2.5 h-2.5" />+24.5%</div>
                </div>
                <div class="rounded-lg bg-white/[0.04] border border-white/[0.06] p-3">
                  <div class="text-[10px] text-white/40 mb-1">Conversioni</div>
                  <div class="text-lg font-bold text-white tabular-nums">{{ cConversions.toFixed(1) }}%</div>
                  <div class="text-[10px] text-green-400 flex items-center gap-0.5"><TrendingUp class="w-2.5 h-2.5" />+180%</div>
                </div>
                <div class="rounded-lg bg-white/[0.04] border border-white/[0.06] p-3">
                  <div class="text-[10px] text-white/40 mb-1">Revenue</div>
                  <div class="text-lg font-bold text-white tabular-nums">CHF {{ cRevenue.toFixed(1) }}k</div>
                  <div class="text-[10px] text-green-400 flex items-center gap-0.5"><TrendingUp class="w-2.5 h-2.5" />+67%</div>
                </div>
              </div>
              <!-- SVG line chart that draws itself + bar chart -->
              <div class="grid grid-cols-2 gap-3">
                <div class="rounded-lg bg-white/[0.04] border border-white/[0.06] p-4">
                  <div class="text-[10px] text-white/40 mb-2">Crescita organica</div>
                  <svg viewBox="0 0 200 60" class="w-full h-16">
                    <defs><linearGradient id="lg" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stop-color="#60a5fa" stop-opacity="0.3"/><stop offset="100%" stop-color="#3b82f6"/></linearGradient></defs>
                    <path d="M0,55 C20,52 35,48 55,40 C75,32 90,28 110,22 C130,16 150,12 170,8 C185,5 195,3 200,2" fill="none" stroke="url(#lg)" stroke-width="2" class="line-draw" />
                    <circle cx="200" cy="2" r="3" fill="#3b82f6" class="line-dot" />
                  </svg>
                </div>
                <div class="rounded-lg bg-white/[0.04] border border-white/[0.06] p-4">
                  <div class="text-[10px] text-white/40 mb-2">Performance mensile</div>
                  <div class="flex items-end gap-1.5 h-16">
                    <div class="flex-1 rounded-t bar-grow bg-white/10" style="--h: 35%"></div>
                    <div class="flex-1 rounded-t bar-grow bg-white/10" style="--h: 50%"></div>
                    <div class="flex-1 rounded-t bar-grow bg-white/15" style="--h: 42%"></div>
                    <div class="flex-1 rounded-t bar-grow bg-white/15" style="--h: 65%"></div>
                    <div class="flex-1 rounded-t bar-grow bg-white/20" style="--h: 55%"></div>
                    <div class="flex-1 rounded-t bar-grow bg-blue-500/40" style="--h: 70%"></div>
                    <div class="flex-1 rounded-t bar-grow bg-blue-500/50" style="--h: 85%"></div>
                    <div class="flex-1 rounded-t bar-grow bg-blue-500/70" style="--h: 90%"></div>
                    <div class="flex-1 rounded-t bar-grow bg-blue-500" style="--h: 100%"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Stats row -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
          <div v-for="(stat, index) in impressiveStats" :key="index" class="text-center anim-el" :class="'d' + (index + 3)">
            <div class="text-4xl md:text-5xl font-extrabold text-white mb-2">{{ stat.value }}</div>
            <div class="text-sm font-semibold text-white mb-1">{{ stat.label }}</div>
            <div class="text-xs text-white/60">{{ stat.description }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features + animated responsive device mockup -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="features" :class="{ 'is-visible': visible.features }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('website.features.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">{{ $t('website.features.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">{{ $t('website.features.subtitle') }}</p>
        </div>

        <!-- Device mockup: Desktop + Phone side by side -->
        <div class="max-w-4xl mx-auto mb-16 flex items-end justify-center gap-4">
          <!-- Desktop mockup -->
          <div class="flex-1 max-w-lg anim-el d1">
            <div class="mockup-browser rounded-xl border border-gray-200 dark:border-white/[0.08] overflow-hidden bg-white dark:bg-dark transition-colors duration-300">
              <div class="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                <div class="flex gap-1"><div class="w-2 h-2 rounded-full bg-red-400/60"></div><div class="w-2 h-2 rounded-full bg-yellow-400/60"></div><div class="w-2 h-2 rounded-full bg-green-400/60"></div></div>
                <div class="flex-1 h-5 rounded bg-white dark:bg-white/[0.06] mx-6"></div>
              </div>
              <div class="p-4 space-y-3">
                <span class="text-[9px] font-bold text-gray-900/40 dark:text-white/30">DesignStudio</span>
                <div class="h-20 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-gray-100 dark:border-white/[0.04] flex items-center justify-center">
                  <div class="text-center">
                    <p class="text-[10px] font-bold text-gray-600 dark:text-gray-300 mb-1">Benvenuto nel futuro</p>
                    <p class="text-[7px] text-gray-400 dark:text-gray-500 mb-2">Esplora le nostre soluzioni</p>
                    <div class="h-5 w-20 rounded bg-blue-500/80 mx-auto flex items-center justify-center"><span class="text-[7px] text-white font-bold">Scopri</span></div>
                  </div>
                </div>
                <div class="grid grid-cols-3 gap-2">
                  <div class="h-12 rounded bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] flex items-center justify-center"><span class="text-[7px] text-gray-400">Design</span></div>
                  <div class="h-12 rounded bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] flex items-center justify-center"><span class="text-[7px] text-gray-400">Sviluppo</span></div>
                  <div class="h-12 rounded bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] flex items-center justify-center"><span class="text-[7px] text-gray-400">SEO</span></div>
                </div>
              </div>
            </div>
          </div>
          <!-- Phone mockup -->
          <div class="w-28 flex-shrink-0 anim-el d2">
            <div class="rounded-2xl border-2 border-gray-300 dark:border-white/[0.15] overflow-hidden bg-white dark:bg-dark p-1.5 transition-colors duration-300">
              <div class="w-8 h-1 rounded-full bg-gray-200 dark:bg-white/[0.1] mx-auto mb-2"></div>
              <div class="space-y-2 px-1">
                <p class="text-[6px] font-bold text-gray-500 dark:text-gray-400">Benvenuto</p>
                <div class="h-10 rounded bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-gray-100 dark:border-white/[0.04] flex items-center justify-center"><span class="text-[5px] text-gray-400">Hero Image</span></div>
                <p class="text-[5px] text-gray-400 dark:text-gray-500">Design premium per mobile</p>
                <p class="text-[5px] text-gray-400 dark:text-gray-500">100% responsive</p>
                <div class="grid grid-cols-2 gap-1">
                  <div class="h-8 rounded bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] flex items-center justify-center"><span class="text-[5px] text-gray-400">Servizio 1</span></div>
                  <div class="h-8 rounded bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] flex items-center justify-center"><span class="text-[5px] text-gray-400">Servizio 2</span></div>
                </div>
                <div class="h-4 w-16 rounded bg-blue-500/80 mx-auto flex items-center justify-center"><span class="text-[5px] text-white font-bold">CTA</span></div>
              </div>
              <div class="w-6 h-1 rounded-full bg-gray-200 dark:bg-white/[0.1] mx-auto mt-2"></div>
            </div>
          </div>
        </div>

        <!-- Feature cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div v-for="(feature, idx) in features" :key="feature.title" class="rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300 anim-el" :class="'d' + (idx + 3)">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center mb-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
              <component :is="feature.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
            <h3 class="text-lg font-bold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ feature.title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Problems vs Solutions + before/after browser mockups -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="problems" :class="{ 'is-visible': visible.problems }">
      <div class="container mx-auto px-4">
        <div class="max-w-3xl mx-auto text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('website.whatIsWebsite.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">{{ $t('website.whatIsWebsite.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('website.whatIsWebsite.subtitle') }}</p>
        </div>

        <!-- Before / After browser mockups -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
          <!-- "Before" - broken site with glitch -->
          <div class="anim-el d1">
            <div class="text-center mb-3"><span class="text-xs font-bold uppercase tracking-wider text-red-400">{{ tr('Prima', 'Before') }}</span></div>
            <div class="rounded-xl border border-red-200 dark:border-red-500/20 overflow-hidden bg-white dark:bg-dark opacity-80 transition-colors duration-300 glitch-shake">
              <div class="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                <div class="flex gap-1"><div class="w-2 h-2 rounded-full bg-red-400/60"></div><div class="w-2 h-2 rounded-full bg-yellow-400/60"></div><div class="w-2 h-2 rounded-full bg-green-400/60"></div></div>
                <div class="flex-1 h-5 rounded bg-white dark:bg-white/[0.06] mx-4 flex items-center px-2"><span class="text-[10px] text-red-400">⚠ non sicuro</span></div>
              </div>
              <div class="p-4 space-y-3">
                <div class="h-1 rounded-full bg-gray-100 dark:bg-white/[0.04] overflow-hidden"><div class="h-full w-1/3 bg-red-400/60 rounded-full loading-stuck"></div></div>
                <div class="flex gap-4">
                  <span class="text-[9px] text-gray-300 dark:text-gray-600">Home</span>
                  <span class="text-[9px] text-gray-300 dark:text-gray-600 mt-1">Servzi</span>
                  <span class="text-[9px] text-gray-300 dark:text-gray-600 -mt-0.5">Contati</span>
                </div>
                <div class="h-16 rounded bg-gray-100 dark:bg-white/[0.04] border border-dashed border-gray-300 dark:border-white/[0.08] flex items-center justify-center">
                  <span class="text-[10px] text-gray-300 dark:text-gray-600">⚠ Immagine non caricata</span>
                </div>
                <p class="text-[8px] text-gray-300 dark:text-gray-600">Lorem ipsum dolor sit amet consectetur adipisicing...</p>
                <p class="text-[8px] text-gray-300 dark:text-gray-600">Testo non leggibile su mobile, font troppo piccolo</p>
                <div class="flex gap-2 mt-2">
                  <div class="h-6 w-20 rounded bg-gray-200 dark:bg-white/[0.06] flex items-center justify-center"><span class="text-[7px] text-gray-400">Clicca qui</span></div>
                  <div class="h-6 w-16 rounded bg-gray-200 dark:bg-white/[0.06] flex items-center justify-center"><span class="text-[7px] text-gray-400">Info</span></div>
                </div>
              </div>
            </div>
          </div>
          <!-- "After" - modern site with glow -->
          <div class="anim-el d2">
            <div class="text-center mb-3"><span class="text-xs font-bold uppercase tracking-wider text-green-500">{{ tr('Dopo', 'After') }}</span></div>
            <div class="rounded-xl border border-green-200 dark:border-green-500/20 overflow-hidden bg-white dark:bg-dark transition-colors duration-300 after-glow">
              <div class="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                <div class="flex gap-1"><div class="w-2 h-2 rounded-full bg-red-400/60"></div><div class="w-2 h-2 rounded-full bg-yellow-400/60"></div><div class="w-2 h-2 rounded-full bg-green-400/60"></div></div>
                <div class="flex-1 h-5 rounded bg-white dark:bg-white/[0.06] mx-4 flex items-center px-2"><Lock class="w-2.5 h-2.5 text-green-500 mr-1" /><span class="text-[10px] text-gray-400">tuodominio.com</span></div>
              </div>
              <div class="p-4 space-y-3">
                <div class="h-1 rounded-full bg-gray-100 dark:bg-white/[0.04] overflow-hidden"><div class="h-full w-full bg-green-500 rounded-full loading-done"></div></div>
                <div class="flex items-center justify-between">
                  <span class="text-[9px] font-bold text-gray-900/50 dark:text-white/40">TuoBrand</span>
                  <div class="flex gap-3 items-center">
                    <span class="text-[8px] text-gray-400 dark:text-gray-500">Servizi</span>
                    <span class="text-[8px] text-gray-400 dark:text-gray-500">Portfolio</span>
                    <div class="h-5 w-14 rounded bg-blue-500/80 flex items-center justify-center"><span class="text-[7px] text-white font-bold">Contatti</span></div>
                  </div>
                </div>
                <div class="h-16 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-gray-100 dark:border-white/[0.04] flex items-center justify-center">
                  <p class="text-[9px] font-bold text-gray-600 dark:text-gray-300">Cresci con noi oggi</p>
                </div>
                <p class="text-[8px] text-gray-400 dark:text-gray-500">Design moderno che converte visitatori in clienti reali.</p>
                <p class="text-[8px] text-gray-400 dark:text-gray-500">Ottimizzato per ogni dispositivo e motore di ricerca.</p>
                <div class="flex gap-2 mt-2">
                  <div class="h-6 w-20 rounded-md bg-gray-900 dark:bg-white flex items-center justify-center"><span class="text-[7px] font-bold text-white dark:text-gray-900">Inizia</span></div>
                  <div class="h-6 w-16 rounded-md border border-gray-200 dark:border-white/[0.08] flex items-center justify-center"><span class="text-[7px] font-bold text-gray-500 dark:text-gray-400">Demo</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Problems / Solutions text columns -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          <div>
            <div class="flex items-center gap-2 mb-8">
              <div class="h-px flex-1 bg-gray-200 dark:bg-white/[0.08]"></div>
              <span class="text-[13px] uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400 font-medium">{{ $t('website.whatIsWebsite.problems.title') }}</span>
              <div class="h-px flex-1 bg-gray-200 dark:bg-white/[0.08]"></div>
            </div>
            <div class="space-y-4">
              <div class="flex items-start gap-3 anim-el d3">
                <span class="text-red-400 text-xl font-bold flex-shrink-0">×</span>
                <div>
                  <p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('website.whatIsWebsite.problems.outdated.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('website.whatIsWebsite.problems.outdated.description') }}</p>
                </div>
              </div>
              <div class="flex items-start gap-3 anim-el d4">
                <span class="text-red-400 text-xl font-bold flex-shrink-0">×</span>
                <div>
                  <p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('website.whatIsWebsite.problems.mobile.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('website.whatIsWebsite.problems.mobile.description') }}</p>
                </div>
              </div>
              <div class="flex items-start gap-3 anim-el d5">
                <span class="text-red-400 text-xl font-bold flex-shrink-0">×</span>
                <div>
                  <p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('website.whatIsWebsite.problems.seo.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('website.whatIsWebsite.problems.seo.description') }}</p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div class="flex items-center gap-2 mb-8">
              <div class="h-px flex-1 bg-blue-200 dark:bg-blue-500/20"></div>
              <span class="text-[13px] uppercase tracking-[0.15em] text-blue-500 font-medium">{{ $t('website.whatIsWebsite.solutions.title') }}</span>
              <div class="h-px flex-1 bg-blue-200 dark:bg-blue-500/20"></div>
            </div>
            <div class="space-y-4">
              <div class="flex items-start gap-3 anim-el d3">
                <Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('website.whatIsWebsite.solutions.modern.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('website.whatIsWebsite.solutions.modern.description') }}</p>
                </div>
              </div>
              <div class="flex items-start gap-3 anim-el d4">
                <Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('website.whatIsWebsite.solutions.responsive.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('website.whatIsWebsite.solutions.responsive.description') }}</p>
                </div>
              </div>
              <div class="flex items-start gap-3 anim-el d5">
                <Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('website.whatIsWebsite.solutions.optimized.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('website.whatIsWebsite.solutions.optimized.description') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Process (timeline with step illustrations) -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="process" :class="{ 'is-visible': visible.process }">
      <div class="container mx-auto px-4">
        <div class="max-w-3xl mx-auto text-center mb-20">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('website.howItWorks.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">{{ $t('website.howItWorks.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('website.howItWorks.subtitle') }}</p>
        </div>

        <div class="max-w-3xl mx-auto space-y-0">
          <div v-for="(step, index) in howItWorksSteps" :key="step.title" class="relative flex gap-6 md:gap-10 anim-el" :class="'d' + (index + 1)">
            <div class="flex flex-col items-center">
              <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.1] flex items-center justify-center text-sm font-bold text-gray-900 dark:text-white flex-shrink-0 transition-colors duration-300">
                {{ index + 1 }}
              </div>
              <div v-if="index < howItWorksSteps.length - 1" class="w-px flex-1 bg-gray-200 dark:bg-white/[0.06] my-2 transition-colors duration-300"></div>
            </div>
            <div class="pb-14 flex-1" :class="{ 'pb-0': index === howItWorksSteps.length - 1 }">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ step.title }}</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4 transition-colors duration-300">{{ step.description }}</p>

              <!-- Step illustrations -->
              <!-- Step 1: Animated checklist -->
              <div v-if="index === 0" class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.02] p-3 max-w-xs transition-colors duration-300">
                <div class="space-y-2">
                  <div class="flex items-center gap-2 transition-all duration-500" :class="checksVisible >= 1 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-3'">
                    <div class="w-4 h-4 rounded flex items-center justify-center transition-colors duration-300" :class="checksVisible >= 1 ? 'border-blue-500 bg-blue-500/10 border' : 'border border-gray-300 dark:border-white/[0.1]'"><Check v-if="checksVisible >= 1" class="w-2.5 h-2.5 text-blue-500" /></div><span class="text-[9px] text-gray-500 dark:text-gray-400">Obiettivi del progetto</span>
                  </div>
                  <div class="flex items-center gap-2 transition-all duration-500" :class="checksVisible >= 2 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-3'">
                    <div class="w-4 h-4 rounded flex items-center justify-center transition-colors duration-300" :class="checksVisible >= 2 ? 'border-blue-500 bg-blue-500/10 border' : 'border border-gray-300 dark:border-white/[0.1]'"><Check v-if="checksVisible >= 2" class="w-2.5 h-2.5 text-blue-500" /></div><span class="text-[9px] text-gray-500 dark:text-gray-400">Target audience</span>
                  </div>
                  <div class="flex items-center gap-2 transition-all duration-500" :class="checksVisible >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-3'">
                    <div class="w-4 h-4 rounded flex items-center justify-center transition-colors duration-300" :class="checksVisible >= 3 ? 'border-blue-500 bg-blue-500/10 border' : 'border border-gray-300 dark:border-white/[0.1]'"><Check v-if="checksVisible >= 3" class="w-2.5 h-2.5 text-blue-500" /></div><span class="text-[9px] text-gray-500 dark:text-gray-400">Competitor analysis</span>
                  </div>
                  <div class="flex items-center gap-2 transition-all duration-500" :class="checksVisible >= 4 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-3'">
                    <div class="w-4 h-4 rounded flex items-center justify-center transition-colors duration-300" :class="checksVisible >= 4 ? 'border-blue-500 bg-blue-500/10 border' : 'border border-gray-300 dark:border-white/[0.1]'"><Check v-if="checksVisible >= 4" class="w-2.5 h-2.5 text-blue-500" /></div><span class="text-[9px] text-gray-500 dark:text-gray-400">Roadmap</span>
                  </div>
                </div>
              </div>

              <!-- Step 2: Design canvas / color palette -->
              <div v-if="index === 1" class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.02] p-3 max-w-xs transition-colors duration-300">
                <p class="text-[8px] text-gray-400 dark:text-gray-500 mb-2">Palette colori</p>
                <div class="flex gap-2 mb-3">
                  <div class="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center"><span class="text-[6px] text-white font-bold">Brand</span></div>
                  <div class="w-8 h-8 rounded-lg bg-gray-900 dark:bg-white flex items-center justify-center"><span class="text-[6px] text-white dark:text-gray-900 font-bold">Text</span></div>
                  <div class="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-600 flex items-center justify-center"><span class="text-[6px] text-gray-500 font-bold">BG</span></div>
                  <div class="w-8 h-8 rounded-lg border border-gray-200 dark:border-white/[0.1] bg-white dark:bg-dark flex items-center justify-center"><span class="text-[6px] text-gray-400 font-bold">Card</span></div>
                </div>
                <p class="text-[8px] text-gray-400 dark:text-gray-500 mb-1.5">Layout wireframe</p>
                <div class="grid grid-cols-3 gap-1.5">
                  <div class="h-6 rounded bg-gray-200 dark:bg-white/[0.06] flex items-center justify-center"><span class="text-[5px] text-gray-400">Nav</span></div>
                  <div class="h-6 rounded bg-blue-500/20 col-span-2 flex items-center justify-center"><span class="text-[5px] text-blue-400">Hero Section</span></div>
                  <div class="h-4 rounded bg-gray-100 dark:bg-white/[0.04] col-span-2 flex items-center justify-center"><span class="text-[5px] text-gray-400">Contenuto</span></div>
                  <div class="h-4 rounded bg-gray-100 dark:bg-white/[0.04] flex items-center justify-center"><span class="text-[5px] text-gray-400">CTA</span></div>
                </div>
              </div>

              <!-- Step 3: Code editor with typing animation -->
              <div v-if="index === 2" class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-900 dark:bg-black/40 p-3 max-w-xs font-mono text-[10px] leading-relaxed overflow-hidden transition-colors duration-300">
                <div class="flex gap-1 mb-2"><div class="w-2 h-2 rounded-full bg-red-400/60"></div><div class="w-2 h-2 rounded-full bg-yellow-400/60"></div><div class="w-2 h-2 rounded-full bg-green-400/60"></div></div>
                <div v-if="codeVisible >= 1" class="code-line"><span class="text-purple-400">&lt;section</span> <span class="text-blue-300">class</span><span class="text-gray-500">=</span><span class="text-green-400">"hero"</span><span class="text-purple-400">&gt;</span><span v-if="codeVisible === 1" class="typing-cursor">|</span></div>
                <div v-if="codeVisible >= 2" class="code-line pl-3"><span class="text-purple-400">&lt;h1&gt;</span><span class="text-gray-300">Tuo Sito</span><span class="text-purple-400">&lt;/h1&gt;</span><span v-if="codeVisible === 2" class="typing-cursor">|</span></div>
                <div v-if="codeVisible >= 3" class="code-line pl-3"><span class="text-purple-400">&lt;p&gt;</span><span class="text-green-300">+300% conversioni</span><span class="text-purple-400">&lt;/p&gt;</span><span v-if="codeVisible === 3" class="typing-cursor">|</span></div>
                <div v-if="codeVisible >= 4" class="code-line"><span class="text-purple-400">&lt;/section&gt;</span><span class="typing-cursor">|</span></div>
                <div v-if="codeVisible === 0" class="text-gray-600">_<span class="typing-cursor">|</span></div>
              </div>

              <!-- Step 4: Launch / rocket -->
              <div v-if="index === 3" class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.02] p-3 max-w-xs transition-colors duration-300">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <Rocket class="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p class="text-[9px] font-bold text-green-600 dark:text-green-400 mb-0.5">Sito Online!</p>
                    <p class="text-[7px] text-gray-400 dark:text-gray-500">Performance in crescita</p>
                  </div>
                  <div class="ml-auto flex gap-0.5">
                    <div class="w-1.5 h-3 rounded-sm bg-green-500/40"></div>
                    <div class="w-1.5 h-5 rounded-sm bg-green-500/60"></div>
                    <div class="w-1.5 h-7 rounded-sm bg-green-500/80"></div>
                    <div class="w-1.5 h-9 rounded-sm bg-green-500"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Industries: Interactive showcase with large preview -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="industries" :class="{ 'is-visible': visible.industries }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('website.industries.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">{{ $t('website.industries.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">{{ $t('website.industries.subtitle') }}</p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
          <!-- Left: Industry selector tabs -->
          <div class="lg:col-span-2 space-y-2 anim-el d1">
            <button
              v-for="(industry, idx) in industries"
              :key="industry.title"
              @click="activeIndustry = idx"
              class="w-full text-left rounded-xl p-4 border transition-all duration-300"
              :class="activeIndustry === idx
                ? 'bg-white dark:bg-white/[0.06] border-gray-300 dark:border-white/[0.12] shadow-sm'
                : 'bg-transparent border-transparent hover:bg-white/60 dark:hover:bg-white/[0.02] hover:border-gray-200 dark:hover:border-white/[0.06]'"
            >
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300"
                  :class="activeIndustry === idx
                    ? 'bg-blue-500/10 border border-blue-500/20'
                    : 'bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06]'">
                  <component :is="industry.icon" class="h-5 w-5 transition-colors duration-300" :class="activeIndustry === idx ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'" />
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-sm font-bold transition-colors duration-300" :class="activeIndustry === idx ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'">{{ industry.title }}</h3>
                  <p v-if="activeIndustry === idx" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed line-clamp-2">{{ industry.description }}</p>
                </div>
                <ChevronDown class="w-4 h-4 -rotate-90 flex-shrink-0 transition-all duration-300" :class="activeIndustry === idx ? 'text-blue-500 rotate-0' : 'text-gray-300 dark:text-gray-600'" />
              </div>
              <!-- Applications list (expanded when active) -->
              <div v-if="activeIndustry === idx" class="mt-3 pt-3 border-t border-gray-100 dark:border-white/[0.06]">
                <ul class="space-y-1.5">
                  <li v-for="app in industry.applications" :key="app" class="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <Check class="h-3 w-3 text-blue-500 mr-2 flex-shrink-0" />
                    <span>{{ app }}</span>
                  </li>
                </ul>
              </div>
            </button>
          </div>

          <!-- Right: Large animated browser preview -->
          <div class="lg:col-span-3 anim-el d2">
            <div class="sticky top-24">
              <div class="rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden bg-white dark:bg-dark shadow-xl transition-colors duration-300">
                <!-- Browser chrome -->
                <div class="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                  <div class="flex gap-1.5"><div class="w-2.5 h-2.5 rounded-full bg-red-400/70"></div><div class="w-2.5 h-2.5 rounded-full bg-yellow-400/70"></div><div class="w-2.5 h-2.5 rounded-full bg-green-400/70"></div></div>
                  <div class="flex-1 h-6 rounded-md bg-white dark:bg-white/[0.06] mx-6 flex items-center px-3">
                    <Lock class="w-3 h-3 text-green-500 mr-1.5" />
                    <span class="text-[11px] text-gray-400 dark:text-gray-500">{{ industries[activeIndustry]?.title?.toLowerCase().replace(/\s/g, '-') }}.com</span>
                  </div>
                </div>

                <!-- Large wireframe previews (keyed for transition) -->
                <div class="relative" style="min-height: 320px">
                  <transition name="preview-fade" mode="out-in">

                    <!-- E-commerce: Full store -->
                    <div v-if="activeIndustry === 0" key="ecom" class="p-5 space-y-4">
                      <div class="flex items-center justify-between">
                        <span class="text-[11px] font-bold text-gray-900/50 dark:text-white/40 tracking-wide">ShopStyle</span>
                        <div class="flex gap-3 items-center">
                          <div class="h-6 w-[140px] rounded-md bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] flex items-center px-2 gap-1.5"><Search class="w-3 h-3 text-gray-300 dark:text-gray-600" /><span class="text-[8px] text-gray-300 dark:text-gray-600">Cerca prodotti...</span></div>
                          <div class="relative"><ShoppingCart class="w-4 h-4 text-gray-400" /><div class="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-blue-500 text-[6px] text-white flex items-center justify-center font-bold">3</div></div>
                        </div>
                      </div>
                      <div class="h-28 rounded-xl bg-gradient-to-br from-blue-500/8 to-purple-500/8 border border-gray-100 dark:border-white/[0.04] flex items-center justify-center">
                        <div class="text-center">
                          <p class="text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Saldi Invernali -40%</p>
                          <p class="text-[9px] text-gray-400 dark:text-gray-500 mb-3">Spedizione gratuita sopra i CHF 50</p>
                          <div class="h-7 w-24 rounded-lg bg-blue-500 mx-auto flex items-center justify-center"><span class="text-[8px] text-white font-bold">Scopri Ora</span></div>
                        </div>
                      </div>
                      <div class="grid grid-cols-4 gap-3">
                        <div v-for="(prod, pi) in [{name:'Sneaker Air Max',price:'CHF 129',clr:'blue'},{name:'Borsa Pelle',price:'CHF 89',clr:'green'},{name:'Orologio Classic',price:'CHF 249',clr:'orange'},{name:'Giacca Wool',price:'CHF 179',clr:'purple'}]" :key="pi" class="rounded-lg bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-2.5">
                          <div class="h-16 rounded-lg mb-2 flex items-center justify-center" :class="`bg-gradient-to-br from-${prod.clr}-500/10 to-${prod.clr}-500/5`">
                            <ShoppingCart class="w-5 h-5 text-gray-200 dark:text-gray-700" />
                          </div>
                          <p class="text-[8px] font-bold text-gray-700 dark:text-gray-300 mb-0.5 truncate">{{ prod.name }}</p>
                          <p class="text-[7px] text-gray-400 dark:text-gray-500 mb-2">Disponibile</p>
                          <div class="flex justify-between items-center"><span class="text-[9px] font-bold text-gray-900/60 dark:text-white/50">{{ prod.price }}</span><div class="h-5 w-10 rounded-md bg-blue-500/80 flex items-center justify-center"><span class="text-[6px] text-white font-bold">Acquista</span></div></div>
                        </div>
                      </div>
                    </div>

                    <!-- Business: Corporate site -->
                    <div v-else-if="activeIndustry === 1" key="biz" class="p-5 space-y-4">
                      <div class="flex items-center justify-between">
                        <span class="text-[11px] font-bold text-gray-900/50 dark:text-white/40 tracking-wide">Innovatech</span>
                        <div class="flex gap-4 items-center"><span class="text-[9px] text-gray-400 dark:text-gray-500">Servizi</span><span class="text-[9px] text-gray-400 dark:text-gray-500">Team</span><span class="text-[9px] text-gray-400 dark:text-gray-500">Blog</span><div class="h-7 w-20 rounded-lg bg-gray-900 dark:bg-white flex items-center justify-center"><span class="text-[8px] font-bold text-white dark:text-gray-900">Contattaci</span></div></div>
                      </div>
                      <div class="h-32 rounded-xl bg-gradient-to-r from-gray-900/5 via-blue-500/5 to-gray-900/5 dark:from-white/[0.04] dark:via-blue-500/8 dark:to-white/[0.04] border border-gray-100 dark:border-white/[0.04] flex items-center px-6">
                        <div class="flex-1">
                          <p class="text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Soluzioni digitali per il tuo business</p>
                          <p class="text-[9px] text-gray-400 dark:text-gray-500 mb-0.5">Aiutiamo le aziende a crescere con strategie innovative</p>
                          <p class="text-[9px] text-gray-400 dark:text-gray-500 mb-4">e tecnologie all'avanguardia.</p>
                          <div class="flex gap-2">
                            <div class="h-7 w-24 rounded-lg bg-gray-900 dark:bg-white flex items-center justify-center"><span class="text-[8px] font-bold text-white dark:text-gray-900">Scopri di più</span></div>
                            <div class="h-7 w-20 rounded-lg border border-gray-200 dark:border-white/[0.08] flex items-center justify-center"><span class="text-[8px] font-bold text-gray-500 dark:text-gray-400">Portfolio</span></div>
                          </div>
                        </div>
                      </div>
                      <div class="grid grid-cols-3 gap-3">
                        <div v-for="(svc, si) in [{icon:'Consulenza',clr:'blue'},{icon:'Sviluppo',clr:'green'},{icon:'Marketing',clr:'purple'}]" :key="si" class="rounded-lg bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-4 text-center">
                          <div class="w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center" :class="`bg-${svc.clr}-500/10`">
                            <component :is="si===0 ? Globe : si===1 ? Settings : Rocket" class="w-4 h-4" :class="`text-${svc.clr}-500`" />
                          </div>
                          <p class="text-[9px] font-bold text-gray-700 dark:text-gray-300 mb-0.5">{{ svc.icon }}</p>
                          <p class="text-[7px] text-gray-400 dark:text-gray-500">Strategica e su misura</p>
                        </div>
                      </div>
                      <div class="rounded-lg bg-gray-50 dark:bg-white/[0.02] border border-gray-100 dark:border-white/[0.04] p-3 flex items-center gap-4">
                        <div class="w-10 h-10 rounded-full bg-blue-500/10 flex-shrink-0 flex items-center justify-center"><span class="text-[11px] font-bold text-blue-500">MC</span></div>
                        <div class="flex-1">
                          <p class="text-[9px] text-gray-600 dark:text-gray-300 italic">"Hanno trasformato la nostra presenza online. +150% lead in 3 mesi."</p>
                          <p class="text-[8px] text-gray-400 dark:text-gray-500 mt-0.5">— Marco C., CEO TechStart</p>
                        </div>
                      </div>
                    </div>

                    <!-- Healthcare: Medical portal -->
                    <div v-else-if="activeIndustry === 2" key="health" class="p-5 space-y-4">
                      <div class="flex items-center justify-between">
                        <div class="flex gap-2 items-center"><div class="w-5 h-5 rounded-full bg-red-500/15 flex items-center justify-center"><span class="text-[8px]">❤</span></div><span class="text-[11px] font-bold text-gray-900/50 dark:text-white/40">MediCare</span></div>
                        <div class="h-7 w-24 rounded-lg bg-green-500 flex items-center justify-center"><span class="text-[8px] text-white font-bold">PRENOTA ORA</span></div>
                      </div>
                      <div class="grid grid-cols-3 gap-3">
                        <div v-for="(doc, di) in [{name:'Dr. Rossi',spec:'Cardiologia',clr:'blue'},{name:'Dr.ssa Bianchi',spec:'Dermatologia',clr:'green'},{name:'Dr. Verdi',spec:'Ortopedia',clr:'purple'}]" :key="di" class="rounded-lg bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-3 text-center">
                          <div class="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center" :class="`bg-${doc.clr}-500/10`">
                            <span class="text-[10px] font-bold" :class="`text-${doc.clr}-500`">{{ doc.name.charAt(0) }}{{ doc.name.split(' ')[1]?.charAt(0) }}</span>
                          </div>
                          <p class="text-[9px] font-bold text-gray-700 dark:text-gray-300 mb-0.5">{{ doc.name }}</p>
                          <p class="text-[7px] text-gray-400 dark:text-gray-500 mb-2">{{ doc.spec }}</p>
                          <div class="h-5 w-full rounded-md flex items-center justify-center" :class="`bg-${doc.clr}-500/80`"><span class="text-[7px] text-white font-bold">Prenota</span></div>
                        </div>
                      </div>
                      <div class="rounded-xl bg-blue-500/5 border border-blue-500/10 p-4">
                        <div class="flex items-center gap-3 mb-3"><div class="w-5 h-5 rounded bg-blue-500/15 flex items-center justify-center"><span class="text-[9px]">📅</span></div><span class="text-[10px] font-bold text-blue-600/70 dark:text-blue-400/70">Prossime disponibilità — Febbraio</span></div>
                        <div class="grid grid-cols-7 gap-1">
                          <div v-for="d in 14" :key="d" class="h-6 rounded text-center text-[7px] flex items-center justify-center" :class="d===5 ? 'bg-blue-500 text-white font-bold' : d===9 ? 'bg-green-500/20 text-green-600 font-medium' : 'bg-gray-100 dark:bg-white/[0.04] text-gray-400'">{{ d + 14 }}</div>
                        </div>
                        <div class="flex gap-4 mt-2">
                          <div class="flex items-center gap-1"><div class="w-2 h-2 rounded-full bg-blue-500"></div><span class="text-[7px] text-gray-400">Selezionato</span></div>
                          <div class="flex items-center gap-1"><div class="w-2 h-2 rounded-full bg-green-500/40"></div><span class="text-[7px] text-gray-400">Disponibile</span></div>
                        </div>
                      </div>
                    </div>

                    <!-- Education: LMS -->
                    <div v-else-if="activeIndustry === 3" key="edu" class="p-5 space-y-4">
                      <div class="flex items-center justify-between">
                        <span class="text-[11px] font-bold text-gray-900/50 dark:text-white/40 tracking-wide">EduPlatform</span>
                        <div class="flex gap-3 items-center"><span class="text-[9px] text-gray-400 dark:text-gray-500">I miei corsi</span><div class="w-6 h-6 rounded-full bg-purple-500/15 flex items-center justify-center"><span class="text-[9px] font-bold text-purple-500">G</span></div></div>
                      </div>
                      <div class="rounded-lg bg-purple-500/5 border border-purple-500/10 p-3 flex items-center gap-4">
                        <div class="flex-1">
                          <p class="text-[10px] font-bold text-gray-700 dark:text-gray-300">Nuovo corso: UX Design Masterclass</p>
                          <p class="text-[8px] text-gray-400 dark:text-gray-500">Iscriviti ora e ottieni il certificato professionale</p>
                        </div>
                        <div class="h-7 w-20 rounded-lg bg-purple-500 flex items-center justify-center"><span class="text-[8px] text-white font-bold">Iscriviti</span></div>
                      </div>
                      <div class="grid grid-cols-2 gap-3">
                        <div v-for="(course, ci) in [{name:'Web Development',cat:'Programmazione',clr:'blue',pct:65},{name:'Digital Marketing',cat:'Business',clr:'green',pct:40},{name:'UI/UX Design',cat:'Design',clr:'orange',pct:80},{name:'Data Science',cat:'Analytics',clr:'purple',pct:25}]" :key="ci" class="rounded-lg bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-3">
                          <div class="h-14 rounded-lg mb-2 flex items-center justify-center" :class="`bg-gradient-to-br from-${course.clr}-500/10 to-${course.clr}-500/5`">
                            <span class="text-[9px] font-bold" :class="`text-${course.clr}-500/60`">{{ course.cat }}</span>
                          </div>
                          <p class="text-[9px] font-bold text-gray-700 dark:text-gray-300 mb-0.5">{{ course.name }}</p>
                          <p class="text-[7px] text-gray-400 dark:text-gray-500 mb-2">12 lezioni · 6h totali</p>
                          <div class="flex items-center gap-2"><div class="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-white/[0.04]"><div class="h-full rounded-full" :style="`width: ${course.pct}%`" :class="`bg-${course.clr}-500/60`"></div></div><span class="text-[8px] text-gray-400">{{ course.pct }}%</span></div>
                        </div>
                      </div>
                    </div>

                    <!-- Finance: Dashboard -->
                    <div v-else-if="activeIndustry === 4" key="fin" class="p-5 space-y-4">
                      <div class="flex items-center justify-between">
                        <div class="flex items-center gap-2"><Lock class="w-3 h-3 text-green-500" /><span class="text-[11px] font-bold text-gray-900/50 dark:text-white/40">FinanzApp</span></div>
                        <div class="flex gap-2 items-center"><span class="text-[8px] text-gray-400">Benvenuto, Luca</span><div class="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center"><span class="text-[9px] font-bold text-blue-500">L</span></div></div>
                      </div>
                      <div class="grid grid-cols-3 gap-3">
                        <div class="rounded-lg bg-green-500/5 border border-green-500/10 p-3"><div class="text-[9px] text-gray-400 mb-0.5">Portfolio</div><div class="text-sm font-bold text-green-600">CHF 124.8k</div><div class="text-[8px] text-green-500">+12.4%</div></div>
                        <div class="rounded-lg bg-blue-500/5 border border-blue-500/10 p-3"><div class="text-[9px] text-gray-400 mb-0.5">Rendimento</div><div class="text-sm font-bold text-blue-600">8.2%</div><div class="text-[8px] text-blue-500">annuo</div></div>
                        <div class="rounded-lg bg-purple-500/5 border border-purple-500/10 p-3"><div class="text-[9px] text-gray-400 mb-0.5">Operazioni</div><div class="text-sm font-bold text-purple-600">847</div><div class="text-[8px] text-purple-500">questo mese</div></div>
                      </div>
                      <div class="rounded-lg bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.04] p-3">
                        <div class="text-[9px] text-gray-400 mb-2">Andamento portafoglio</div>
                        <svg viewBox="0 0 280 70" class="w-full h-20">
                          <defs><linearGradient id="finGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#22c55e" stop-opacity="0.15"/><stop offset="100%" stop-color="#22c55e" stop-opacity="0"/></linearGradient></defs>
                          <path d="M0,60 C30,58 50,50 80,42 C110,34 130,38 160,28 C190,18 220,22 250,10 L280,5 L280,70 L0,70Z" fill="url(#finGrad)" />
                          <path d="M0,60 C30,58 50,50 80,42 C110,34 130,38 160,28 C190,18 220,22 250,10 L280,5" fill="none" stroke="#22c55e" stroke-width="2" class="line-draw" />
                        </svg>
                      </div>
                      <div class="rounded-lg bg-gray-50 dark:bg-white/[0.02] border border-gray-100 dark:border-white/[0.04] p-2 space-y-1.5">
                        <div class="flex items-center justify-between text-[8px] mb-1"><span class="font-bold text-gray-500 dark:text-gray-400">Ultime transazioni</span><span class="text-gray-400">Oggi</span></div>
                        <div v-for="(tx, ti) in [{name:'Dividendi AAPL',amt:'+CHF 2,400',pos:true},{name:'Commissioni ETF',amt:'-CHF 890',pos:false},{name:'Vendita BTC',amt:'+CHF 5,120',pos:true}]" :key="ti" class="flex items-center gap-2 text-[8px]">
                          <div class="w-1.5 h-1.5 rounded-full" :class="tx.pos ? 'bg-green-500' : 'bg-red-400'"></div>
                          <span class="text-gray-500 dark:text-gray-400">{{ tx.name }}</span>
                          <span class="ml-auto font-bold" :class="tx.pos ? 'text-green-500' : 'text-red-400'">{{ tx.amt }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Manufacturing: Product catalog -->
                    <div v-else-if="activeIndustry === 5" key="manuf" class="p-5 space-y-4">
                      <div class="flex items-center justify-between">
                        <span class="text-[11px] font-bold text-gray-900/50 dark:text-white/40 tracking-wide">MetalPro</span>
                        <div class="flex gap-3 items-center"><span class="text-[9px] text-gray-400 dark:text-gray-500">Prodotti</span><span class="text-[9px] text-gray-400 dark:text-gray-500">Catalogo</span><div class="h-7 w-20 rounded-lg bg-gray-900 dark:bg-white flex items-center justify-center"><span class="text-[8px] font-bold text-white dark:text-gray-900">Preventivo</span></div></div>
                      </div>
                      <div class="flex gap-4">
                        <div class="w-2/5">
                          <div class="h-40 rounded-xl bg-gradient-to-b from-gray-200/50 dark:from-white/[0.04] to-gray-100/50 dark:to-white/[0.02] border border-gray-100 dark:border-white/[0.04] mb-2 flex items-center justify-center"><Settings class="w-10 h-10 text-gray-300 dark:text-gray-700 animate-spin" style="animation-duration: 8s" /></div>
                          <div class="flex gap-1">
                            <div v-for="(thumb, ti) in ['Vista 1','Vista 2','Vista 3','3D']" :key="ti" class="flex-1 h-8 rounded bg-gray-100 dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.04] flex items-center justify-center"><span class="text-[6px] text-gray-400">{{ thumb }}</span></div>
                          </div>
                        </div>
                        <div class="flex-1 space-y-3">
                          <div>
                            <p class="text-[11px] font-bold text-gray-700 dark:text-gray-300 mb-0.5">Valvola Industriale VX-200</p>
                            <p class="text-[8px] text-gray-400 dark:text-gray-500">Codice: VX-200-SS-DN50</p>
                          </div>
                          <div class="space-y-1.5">
                            <div v-for="(spec, si) in [{k:'Materiale',v:'Acciaio Inox 316'},{k:'Pressione max',v:'40 bar'},{k:'Temperatura',v:'-20°C / +180°C'},{k:'Certificazioni',v:'CE, ATEX, ISO 9001'}]" :key="si" class="flex justify-between items-center text-[8px] py-1 border-b border-gray-100 dark:border-white/[0.04]">
                              <span class="text-gray-400 dark:text-gray-500">{{ spec.k }}</span>
                              <span class="font-medium text-gray-600 dark:text-gray-300">{{ spec.v }}</span>
                            </div>
                          </div>
                          <div class="flex gap-2">
                            <div class="h-8 w-28 rounded-lg bg-gray-900 dark:bg-white flex items-center justify-center"><span class="text-[8px] font-bold text-white dark:text-gray-900">Richiedi Info</span></div>
                            <div class="h-8 w-24 rounded-lg border border-gray-200 dark:border-white/[0.08] flex items-center justify-center"><span class="text-[8px] font-bold text-gray-500 dark:text-gray-400">Scheda PDF</span></div>
                          </div>
                        </div>
                      </div>
                    </div>

                  </transition>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Advanced Projects -->
    <section class="relative py-28 bg-gray-950 overflow-hidden" data-animate="advanced" :class="{ 'is-visible': visible.advanced }">
      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-blue-500/[0.04] blur-3xl"></div>
        <div class="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-purple-500/[0.04] blur-3xl"></div>
      </div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-4xl mx-auto text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-blue-400/60 font-medium mb-4 anim-el d1">{{ tr('Soluzioni Avanzate', 'Advanced solutions') }}</p>
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 anim-el d1">{{ tr('Progetti Su Misura & Avanzati', 'Custom & advanced projects') }}</h2>
          <p class="text-lg md:text-xl text-white/60 leading-relaxed max-w-2xl mx-auto anim-el d2">{{ tr('Hai esigenze specifiche? Siamo', 'Do you have specific needs? We are') }} <span class="text-white font-semibold">{{ tr('sviluppatori', 'developers') }}</span>, {{ tr('non solo designer. Realizziamo ecosistemi digitali complessi.', 'not just designers. We build complex digital ecosystems.') }}</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          <div v-for="(adv, ai) in advancedServices" :key="ai" class="group rounded-2xl p-6 border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.12] transition-all duration-500 anim-el" :class="'d' + (ai + 2)">
            <div class="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-colors duration-300" :class="adv.bg">
              <component :is="adv.icon" class="h-6 w-6" :class="adv.iconColor" />
            </div>
            <h3 class="text-lg font-bold text-white mb-2">{{ adv.title }}</h3>
            <p class="text-sm text-white/50 leading-relaxed">{{ adv.desc }}</p>
          </div>
        </div>

        <div class="text-center mt-14 anim-el d5">
          <router-link to="/contact" class="inline-flex items-center h-14 px-8 bg-white text-gray-900 rounded-xl font-bold text-lg transition-all duration-300 hover:bg-white/90 hover:scale-[1.02]">
            <Rocket class="mr-3 h-5 w-5" />
            {{ tr('Parliamo del tuo progetto', "Let's talk about your project") }}
          </router-link>
          <p class="text-sm text-white/30 mt-4">{{ tr('Preventivo gratuito e senza impegno', 'Free, no-obligation quote') }}</p>
        </div>
      </div>
    </section>

    <!-- Pricing -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="pricing" :class="{ 'is-visible': visible.pricing }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('website.pricing.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">{{ $t('website.pricing.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">{{ $t('website.pricing.subtitle') }}</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <!-- Showcase (featured) -->
          <div class="relative rounded-2xl p-8 bg-gray-50 dark:bg-white/[0.03] border-2 border-gray-900 dark:border-white transition-colors duration-300 anim-el d1 h-full flex flex-col">
            <div class="flex justify-center -mt-12 mb-4">
              <span class="bg-gray-900 dark:bg-white text-white dark:text-dark text-xs font-bold px-4 py-1 rounded-full">{{ $t('website.pricing.popular') }}</span>
            </div>
            <div class="text-center mb-6 min-h-[140px] flex flex-col justify-center">
              <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('website.pricing.packages.showcase.title') }}</h3>
              <div class="text-sm text-gray-400 line-through mb-1">{{ $t('website.pricing.packages.showcase.oldPrice') }}</div>
              <div class="text-4xl font-extrabold text-gray-900 dark:text-white mb-1 transition-colors duration-300">{{ $t('website.pricing.packages.showcase.price') }}</div>
              <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t('website.pricing.packages.showcase.startingFrom') }}</p>
            </div>
            <ul class="space-y-3 mb-6">
              <li v-for="i in 5" :key="'sc'+i" class="flex items-start"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" /><span class="text-sm text-gray-500 dark:text-gray-400">{{ $t('website.pricing.packages.showcase.feature' + i) }}</span></li>
            </ul>
            <router-link to="/contact" class="block w-full text-center py-3 rounded-xl font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark transition-colors duration-300 mt-auto">{{ $t('website.pricing.cta') }}</router-link>
          </div>
          <!-- Business -->
          <div class="relative rounded-2xl p-8 bg-gray-50 dark:bg-white/[0.03] border-2 border-blue-500/70 dark:border-blue-400/70 transition-colors duration-300 anim-el d2 h-full flex flex-col">
            <div class="flex justify-center -mt-12 mb-4">
              <span class="bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">{{ $t('website.pricing.bestSeller') }}</span>
            </div>
            <div class="text-center mb-6 min-h-[140px] flex flex-col justify-center">
              <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('website.pricing.packages.business.title') }}</h3>
              <div class="text-sm text-gray-400 mb-1 invisible">{{ $t('website.pricing.packages.showcase.oldPrice') }}</div>
              <div class="text-4xl font-extrabold text-gray-900 dark:text-white mb-1 transition-colors duration-300">{{ $t('website.pricing.packages.business.price') }}</div>
              <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t('website.pricing.packages.business.startingFrom') }}</p>
            </div>
            <ul class="space-y-3 mb-6">
              <li v-for="i in 5" :key="'bz'+i" class="flex items-start"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" /><span class="text-sm text-gray-500 dark:text-gray-400">{{ $t('website.pricing.packages.business.feature' + i) }}</span></li>
            </ul>
            <router-link to="/contact" class="block w-full text-center py-3 rounded-xl font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark transition-colors duration-300 mt-auto">{{ $t('website.pricing.cta') }}</router-link>
          </div>
          <!-- E-commerce -->
          <div class="relative rounded-2xl p-8 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300 anim-el d3 h-full flex flex-col">
            <div class="flex justify-center -mt-12 mb-4">
              <span class="bg-purple-600 text-white text-xs font-bold px-4 py-1 rounded-full">{{ $t('website.pricing.forShops') }}</span>
            </div>
            <div class="text-center mb-6 min-h-[140px] flex flex-col justify-center">
              <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('website.pricing.packages.ecommerce.title') }}</h3>
              <div class="text-sm text-gray-400 mb-1 invisible">{{ $t('website.pricing.packages.showcase.oldPrice') }}</div>
              <div class="text-4xl font-extrabold text-gray-900 dark:text-white mb-1 transition-colors duration-300">{{ $t('website.pricing.packages.ecommerce.price') }}</div>
              <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t('website.pricing.packages.ecommerce.startingFrom') }}</p>
            </div>
            <ul class="space-y-3 mb-6">
              <li v-for="i in 5" :key="'ec'+i" class="flex items-start"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" /><span class="text-sm text-gray-500 dark:text-gray-400">{{ $t('website.pricing.packages.ecommerce.feature' + i) }}</span></li>
            </ul>
            <router-link to="/contact" class="block w-full text-center py-3 rounded-xl font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark transition-colors duration-300 mt-auto">{{ $t('website.pricing.cta') }}</router-link>
          </div>
        </div>
        <div class="mt-12 max-w-2xl mx-auto anim-el d4">
          <div class="rounded-2xl p-8 border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] transition-colors duration-300">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">{{ $t('website.pricing.otherSolutions.title') }}</h3>
            <p class="text-gray-500 dark:text-gray-400 mb-6 transition-colors duration-300">{{ $t('website.pricing.otherSolutions.description') }}</p>
            <router-link to="/preventivo-sito-web" class="inline-flex items-center bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl px-8 py-3 font-semibold transition-colors duration-300">{{ $t('website.pricing.customQuote') }}<Globe class="ml-2 h-5 w-5" /></router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('website.faq.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">{{ $t('website.faq.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">{{ $t('website.faq.subtitle') }}</p>
        </div>
        <div class="max-w-3xl mx-auto">
          <div class="divide-y divide-gray-200 dark:divide-white/[0.06]">
            <div v-for="(faq, index) in faqs" :key="index">
              <button type="button" @click="toggleFaq(index)" class="w-full flex justify-between items-center py-5 text-left">
                <h3 class="text-[15px] font-semibold text-gray-900 dark:text-white pr-8 transition-colors duration-300">{{ faq.question }}</h3>
                <ChevronDown class="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-all duration-300" :class="{ 'rotate-180': activeFaqIndex === index }" />
              </button>
              <div v-show="activeFaqIndex === index" class="pb-5 -mt-1">
                <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA (always dark) -->
    <section class="relative py-28 md:py-40 overflow-hidden">
      <div class="absolute inset-0 bg-gray-950">
        <img src="/ciobai_hero_background.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-60" />
      </div>
      <div class="container mx-auto px-4 text-center relative z-10">
        <div class="max-w-5xl mx-auto">
          <div class="inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/[0.08] px-4 py-1.5 text-[13px] font-medium text-gray-300 mb-6">{{ $t('websiteSolution.specialOffer') }}</div>
          <h2 class="text-4xl md:text-5xl font-extrabold text-white mb-4">{{ $t('website.cta.title') }}</h2>
          <p class="text-xl md:text-2xl mb-8 text-white/90 font-semibold max-w-3xl mx-auto">{{ $t('website.cta.description') }}</p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <router-link to="/contact" class="inline-flex items-center h-14 px-8 bg-white text-gray-900 font-bold text-lg rounded-xl"><Rocket class="mr-3 h-6 w-6" />{{ $t('website.cta.primary') }}</router-link>
            <router-link to="/preventivo-sito-web" class="inline-flex items-center h-14 px-8 border border-white/20 text-white rounded-xl font-semibold text-lg"><Globe class="mr-2 h-5 w-5" />{{ $t('websiteSolution.cta.quote') }}</router-link>
          </div>
          <div class="flex flex-wrap justify-center gap-6 text-sm text-white/90">
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.freeChatbot') }}</div>
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.seo') }}</div>
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.support') }}</div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue'
import {
  Globe, Zap, ShoppingCart, Building, Heart, GraduationCap, CreditCard, Settings,
  Check, Smartphone, Search, Palette, Layout, Code, Rocket, Lock, ChevronDown, TrendingUp,
  Database, Users, CalendarCheck, BarChart3
} from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { useTheme } from '../composables/useTheme'

const { isDark } = useTheme()
const { t, locale } = useI18n()
const tr = (it, en) => ((locale.value || '').startsWith('en') ? en : it)
const activeFaqIndex = ref(null)
const activeIndustry = ref(0)
const visible = reactive({})
let observer = null

function toggleFaq(index) {
  activeFaqIndex.value = activeFaqIndex.value === index ? null : index
}

// 3D parallax for hero mockup
const mx = ref(0)
const my = ref(0)
const heroMockup = ref(null)

function onHeroMove(e) {
  if (!heroMockup.value) return
  const r = heroMockup.value.getBoundingClientRect()
  mx.value = ((e.clientX - r.left) / r.width - 0.5) * 2
  my.value = ((e.clientY - r.top) / r.height - 0.5) * 2
}
function onHeroLeave() { mx.value = 0; my.value = 0 }

const mockupTransform = computed(() => ({
  transform: `perspective(1200px) rotateY(${mx.value * 5}deg) rotateX(${-my.value * 5}deg)`,
  transition: mx.value === 0 && my.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))
const badge1Transform = computed(() => ({
  transform: `translate(${-mx.value * 12}px, ${-my.value * 12}px)`,
  transition: mx.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))
const badge2Transform = computed(() => ({
  transform: `translate(${-mx.value * 18}px, ${-my.value * 18}px)`,
  transition: mx.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))
const badge3Transform = computed(() => ({
  transform: `translate(${-mx.value * 15}px, ${-my.value * 15 - (mx.value === 0 ? 0 : 0)}px) translateY(-50%)`,
  transition: mx.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))

// Animated counters
const cVisitors = ref(0)
const cConversions = ref(0)
const cRevenue = ref(0)

function countUp(target, end, duration, decimals = 0) {
  const s = performance.now()
  function tick(now) {
    const p = Math.min((now - s) / duration, 1)
    const e = 1 - Math.pow(1 - p, 3)
    target.value = parseFloat((e * end).toFixed(decimals))
    if (p < 1) requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

watch(() => visible.valueprop, v => {
  if (v) {
    countUp(cVisitors, 12847, 2200)
    countUp(cConversions, 3.8, 1800, 1)
    countUp(cRevenue, 48.2, 2400, 1)
  }
})

// Typing animation for code editor
const codeVisible = ref(0)
watch(() => visible.process, v => {
  if (v) {
    codeVisible.value = 0
    ;[600, 1200, 1800, 2400].forEach((d, i) => setTimeout(() => { codeVisible.value = i + 1 }, d))
  }
})

// Checklist animation
const checksVisible = ref(0)
watch(() => visible.process, v => {
  if (v) {
    checksVisible.value = 0
    ;[300, 700, 1100, 1500].forEach((d, i) => setTimeout(() => { checksVisible.value = i + 1 }, d))
  }
})

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        visible[entry.target.dataset.animate] = true
      }
    })
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })

  document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el))
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const howItWorksSteps = [
  { icon: Layout, title: t('website.howItWorks.step1.title'), description: t('website.howItWorks.step1.description') },
  { icon: Palette, title: t('website.howItWorks.step2.title'), description: t('website.howItWorks.step2.description') },
  { icon: Code, title: t('website.howItWorks.step3.title'), description: t('website.howItWorks.step3.description') },
  { icon: Rocket, title: t('website.howItWorks.step4.title'), description: t('website.howItWorks.step4.description') }
]

const features = [
  { icon: Smartphone, title: t('website.features.responsive.title'), description: t('website.features.responsive.description') },
  { icon: Zap, title: t('website.features.performance.title'), description: t('website.features.performance.description') },
  { icon: Search, title: t('website.features.seo.title'), description: t('website.features.seo.description') },
  { icon: Lock, title: t('website.features.security.title'), description: t('website.features.security.description') },
  { icon: Palette, title: t('website.features.design.title'), description: t('website.features.design.description') },
  { icon: Globe, title: t('website.features.multilingual.title'), description: t('website.features.multilingual.description') }
]

const industries = [
  { icon: ShoppingCart, title: t('website.industries.ecommerce.title'), description: t('website.industries.ecommerce.description'), applications: [t('website.industries.ecommerce.app1'), t('website.industries.ecommerce.app2'), t('website.industries.ecommerce.app3')] },
  { icon: Building, title: t('website.industries.business.title'), description: t('website.industries.business.description'), applications: [t('website.industries.business.app1'), t('website.industries.business.app2'), t('website.industries.business.app3')] },
  { icon: Heart, title: t('website.industries.healthcare.title'), description: t('website.industries.healthcare.description'), applications: [t('website.industries.healthcare.app1'), t('website.industries.healthcare.app2'), t('website.industries.healthcare.app3')] },
  { icon: GraduationCap, title: t('website.industries.education.title'), description: t('website.industries.education.description'), applications: [t('website.industries.education.app1'), t('website.industries.education.app2'), t('website.industries.education.app3')] },
  { icon: CreditCard, title: t('website.industries.finance.title'), description: t('website.industries.finance.description'), applications: [t('website.industries.finance.app1'), t('website.industries.finance.app2'), t('website.industries.finance.app3')] },
  { icon: Settings, title: t('website.industries.manufacturing.title'), description: t('website.industries.manufacturing.description'), applications: [t('website.industries.manufacturing.app1'), t('website.industries.manufacturing.app2'), t('website.industries.manufacturing.app3')] }
]

const advancedServices = computed(() => [
  { icon: ShoppingCart, title: tr('E-Commerce & Shop Online', 'E-Commerce & Online Shop'), desc: tr('Vendita prodotti fisici o digitali con pagamenti sicuri, gestione inventario e dashboard ordini.', 'Sell physical or digital products with secure payments, inventory management, and order dashboards.'), bg: 'bg-blue-500/10', iconColor: 'text-blue-400' },
  { icon: CalendarCheck, title: tr('Sistemi di Booking', 'Booking systems'), desc: tr('Prenotazioni appuntamenti, hotel, servizi con calendario interattivo e notifiche automatiche.', 'Appointments, hotel, and service bookings with interactive calendars and automatic notifications.'), bg: 'bg-green-500/10', iconColor: 'text-green-400' },
  { icon: Users, title: tr('Aree Riservate / Clienti', 'Private / Client areas'), desc: tr('Portali con login sicuro, gestione dati personali, documenti e comunicazioni riservate.', 'Portals with secure login, personal data management, documents, and private communications.'), bg: 'bg-purple-500/10', iconColor: 'text-purple-400' },
  { icon: Database, title: tr('Integrazioni API & Gestionali', 'API & business integrations'), desc: tr('Connettiamo il sito ai tuoi software: CRM, ERP, fatturazione elettronica e molto altro.', 'We connect your website to your software: CRM, ERP, e-invoicing, and more.'), bg: 'bg-orange-500/10', iconColor: 'text-orange-400' },
  { icon: BarChart3, title: tr('SEO Avanzato & Performance', 'Advanced SEO & performance'), desc: tr('Ottimizzazione tecnica profonda: Core Web Vitals, schema markup, velocità e indicizzazione.', 'Deep technical optimization: Core Web Vitals, schema markup, speed, and indexing.'), bg: 'bg-cyan-500/10', iconColor: 'text-cyan-400' },
  { icon: Code, title: tr('Sviluppo Custom', 'Custom development'), desc: tr('Funzionalità su misura che non trovi nei template: automazioni, dashboard, strumenti interni.', 'Tailored features you cannot get from templates: automations, dashboards, and internal tools.'), bg: 'bg-pink-500/10', iconColor: 'text-pink-400' }
])

const impressiveStats = computed(() => [
  { value: '300%', label: t('website.stats.growth') || tr('Crescita', 'Growth'), description: t('website.stats.growthDesc') || tr('Aumento conversioni', 'Conversion increase') },
  { value: '<2s', label: t('website.stats.speed') || tr('Velocità', 'Speed'), description: t('website.stats.speedDesc') || tr('Tempo di caricamento', 'Loading time') },
  { value: '100%', label: t('website.stats.responsive') || tr('Responsive', 'Responsive'), description: t('website.stats.responsiveDesc') || tr('Su tutti i dispositivi', 'Across all devices') },
  { value: '24/7', label: t('website.stats.support') || tr('Supporto', 'Support'), description: t('website.stats.supportDesc') || tr('Assistenza continua', 'Continuous assistance') }
])

const faqs = [
  { question: t('website.faq.q1.question'), answer: t('website.faq.q1.answer') },
  { question: t('website.faq.q2.question'), answer: t('website.faq.q2.answer') },
  { question: t('website.faq.q3.question'), answer: t('website.faq.q3.answer') },
  { question: t('website.faq.q4.question'), answer: t('website.faq.q4.answer') },
  { question: t('website.faq.q5.question'), answer: t('website.faq.q5.answer') },
  { question: t('website.faq.q6.question'), answer: t('website.faq.q6.answer') }
]

defineOptions({ name: 'WebsitePage' })
</script>

<style scoped>
.hero-gradient-text {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Tabular numbers for counters */
.tabular-nums { font-variant-numeric: tabular-nums; }

/* Scroll-triggered entrance animations */
.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el {
  opacity: 1;
  transform: translateY(0);
}
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.15s; }
.d3 { transition-delay: 0.25s; }
.d4 { transition-delay: 0.35s; }
.d5 { transition-delay: 0.45s; }
.d6 { transition-delay: 0.55s; }
.d7 { transition-delay: 0.65s; }
.d8 { transition-delay: 0.75s; }
.d9 { transition-delay: 0.85s; }

/* 3D mockup perspective */
.mockup-browser {
  will-change: transform;
  transform-style: preserve-3d;
}

/* Floating badges use inline parallax styles now, no CSS animation needed */
.floating-badge {
  will-change: transform;
}

/* Floating particles (value prop dark section) */
.particle {
  position: absolute;
  bottom: -10px;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: rgba(96, 165, 250, 0.4);
  animation: particleRise linear infinite;
}
@keyframes particleRise {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  5% { opacity: 0.8; }
  50% { transform: translateY(-50vh) translateX(20px); opacity: 0.4; }
  100% { transform: translateY(-100vh) translateX(-10px); opacity: 0; }
}

/* SVG line chart draw animation */
.line-draw {
  stroke-dasharray: 320;
  stroke-dashoffset: 320;
  transition: stroke-dashoffset 2.5s cubic-bezier(.22,1,.36,1) 0.3s;
}
.is-visible .line-draw {
  stroke-dashoffset: 0;
}
.line-dot {
  opacity: 0;
  transition: opacity 0.3s ease 2.5s;
}
.is-visible .line-dot {
  opacity: 1;
}

/* Dashboard bar chart grow */
.bar-grow {
  height: 0;
  transition: height 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.is-visible .bar-grow {
  height: var(--h);
}
.bar-grow:nth-child(1)  { transition-delay: 0.1s; }
.bar-grow:nth-child(2)  { transition-delay: 0.16s; }
.bar-grow:nth-child(3)  { transition-delay: 0.22s; }
.bar-grow:nth-child(4)  { transition-delay: 0.28s; }
.bar-grow:nth-child(5)  { transition-delay: 0.34s; }
.bar-grow:nth-child(6)  { transition-delay: 0.4s; }
.bar-grow:nth-child(7)  { transition-delay: 0.46s; }
.bar-grow:nth-child(8)  { transition-delay: 0.52s; }
.bar-grow:nth-child(9)  { transition-delay: 0.58s; }

/* Before mockup: subtle glitch shake */
.glitch-shake {
  animation: glitchShake 4s ease-in-out infinite;
}
@keyframes glitchShake {
  0%, 92%, 100% { transform: translate(0, 0); }
  93% { transform: translate(-2px, 1px); }
  94% { transform: translate(2px, -1px); }
  95% { transform: translate(-1px, -1px); }
  96% { transform: translate(1px, 1px); }
  97% { transform: translate(0, 0); }
}

/* After mockup: soft glow pulse */
.after-glow {
  animation: glowPulse 3s ease-in-out infinite;
}
@keyframes glowPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
  50% { box-shadow: 0 0 20px 2px rgba(34, 197, 94, 0.08); }
}

/* Loading indicators */
.loading-stuck {
  animation: loadingStuck 2s ease-in-out infinite;
}
@keyframes loadingStuck {
  0%, 100% { width: 30%; }
  50% { width: 38%; }
}
.loading-done {
  width: 0;
  transition: width 1.2s cubic-bezier(.22,1,.36,1) 0.4s;
}
.is-visible .loading-done {
  width: 100%;
}

/* Code editor: typing cursor blink + line entrance */
.typing-cursor {
  animation: cursorBlink 1s step-end infinite;
  color: #60a5fa;
  font-weight: normal;
}
@keyframes cursorBlink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
.code-line {
  animation: codeFadeIn 0.3s ease both;
}
@keyframes codeFadeIn {
  from { opacity: 0; transform: translateX(-6px); }
  to { opacity: 1; transform: translateX(0); }
}

/* Industry preview transition */
.preview-fade-enter-active {
  transition: opacity 0.35s ease, transform 0.35s cubic-bezier(.22,1,.36,1);
}
.preview-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.preview-fade-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
.preview-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Line clamp for descriptions */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
